import { Textarea } from '@/components/ui/textarea';
import { Dropdown } from 'primereact/dropdown';
import { notify } from '@/lib/toast';
import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Plus, Factory, Play, CheckCircle2, XCircle, Clock, ChevronRight, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from 'primereact/dialog';
import { useAuthStore } from '@/stores/auth.store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';
import { FilterChips } from '@/components/ui/filter-chips';

interface ScheduleRunBody {
  recipeId: string;
  locationId: string;
  scheduledQuantity: number;
  notes?: string;
}

interface ProductionRunRow {
  id: string;
  runNumber: string;
  status: string;
  scheduledQuantity: number | string;
  actualYield?: number | string | null;
  plannedCost?: number | string | null;
  variancePercent?: number | string | null;
  recipe?: { name: string; finishedProduct?: { name: string } | null } | null;
  location?: { name: string } | null;
}

interface RecipeOption {
  id: string;
  name: string;
  finishedProduct?: { name: string } | null;
}

interface LocationOption {
  id: string;
  name: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: LucideIcon }> = {
  DRAFT:       { label: 'Draft',       color: 'bg-muted text-muted-foreground',       icon: Clock },
  SCHEDULED:   { label: 'Scheduled',   color: 'bg-info/10 text-info-text',    icon: Clock },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-warning/10 text-warning-text', icon: Play },
  COMPLETED:   { label: 'Completed',   color: 'bg-success/10 text-success-text', icon: CheckCircle2 },
  ON_HOLD:     { label: 'On Hold',     color: 'bg-primary/10 text-primary-text', icon: Clock },
  CANCELLED:   { label: 'Cancelled',   color: 'bg-destructive/10 text-destructive-text',        icon: XCircle },
};

export function ProductionPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [form, setForm] = useState({ recipeId: '', locationId: '', scheduledQuantity: '1', notes: '' });

  const qc = useQueryClient();
  const handleProductionUpdate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['production-runs'] });
    qc.invalidateQueries({ queryKey: ['recipes-active'] });
  }, [qc]);
  useSocketEvent('stock.updated', handleProductionUpdate);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('production:create') || hasPermission('production:*') || hasPermission('*');

  const { data, isLoading } = useQuery({
    queryKey: ['production-runs', page, statusFilter],
    queryFn: () => api.get('/production-runs', { params: { page, limit: 20, status: statusFilter || undefined } }).then(r => r.data),
    staleTime: 30_000,
  });

  const { data: recipesData } = useQuery({
    queryKey: ['recipes-active'],
    queryFn: () => api.get('/recipes', { params: { limit: 100, status: 'ACTIVE' } }).then(r => r.data),
    staleTime: 60_000,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then(r => r.data),
    staleTime: 60_000,
  });

  const scheduleMutation = useMutation({
    mutationFn: (body: ScheduleRunBody) => api.post('/production-runs', body).then(r => r.data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['production-runs'] });
      toast.success('Production run scheduled');
      setDialogOpen(false);
      navigate(`/manufacturing/production/${created.id}`);
    },
    onError: (err) => notify.error(err, 'Failed to schedule run'),
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/production-runs/${id}/start`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-runs'] }); toast.success('Run started'); },
    onError: (err) => notify.error(err, 'Failed to start run'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/production-runs/${id}/cancel`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-runs'] }); toast.success('Run cancelled'); },
    onError: (err) => notify.error(err, 'Failed to cancel run'),
  });

  const handleSchedule = () => {
    if (!form.recipeId) { toast.error('Select a recipe'); return; }
    if (!form.locationId) { toast.error('Select a location'); return; }
    if (Number(form.scheduledQuantity) <= 0) { toast.error('Quantity must be > 0'); return; }
    scheduleMutation.mutate({
      recipeId: form.recipeId,
      locationId: form.locationId,
      scheduledQuantity: Number(form.scheduledQuantity),
      notes: form.notes || undefined,
    });
  };

  const openNew = () => {
    setForm({ recipeId: '', locationId: '', scheduledQuantity: '1', notes: '' });
    setDialogOpen(true);
  };

  const runs = data?.data ?? [];
  const recipes: RecipeOption[] = recipesData?.data ?? [];
  const locations: LocationOption[] = Array.isArray(locationsData) ? locationsData : (locationsData?.data ?? []);

  const recipeFromQuery = searchParams.get('recipe');
  useEffect(() => {
    if (!recipeFromQuery) return;
    setForm((f) => ({ ...f, recipeId: recipeFromQuery }));
    setDialogOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('recipe');
      return next;
    }, { replace: true });
  }, [recipeFromQuery, setSearchParams]);

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-md">
              <Factory className="h-5 w-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Production</h1>
          </div>
          <p className="text-sm text-muted-foreground">Schedule and track production batches</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" /> Schedule Run
          </Button>
        )}
      </div>

      <FilterChips
        chips={[
          { key: '', label: 'All' },
          { key: 'SCHEDULED', label: 'Scheduled' },
          { key: 'IN_PROGRESS', label: 'In progress' },
          { key: 'COMPLETED', label: 'Completed' },
          { key: 'CANCELLED', label: 'Cancelled' },
        ]}
        value={statusFilter}
        onChange={(key) => { setStatusFilter(key); setPage(1); }}
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Card key={i} className="h-24 animate-pulse bg-muted" />)}
        </div>
      ) : runs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Factory className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="font-medium text-muted-foreground">No production runs</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Schedule your first run to start converting raw materials into finished goods</p>
            {canWrite && <Button className="mt-4 gap-2" onClick={openNew}><Plus className="h-4 w-4" /> Schedule Run</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {runs.map((run: ProductionRunRow) => {
            const cfg = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.DRAFT;
            const StatusIcon = cfg.icon;
            return (
              <Card key={run.id} className="group hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/manufacturing/production/${run.id}`)}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{run.runNumber}</p>
                        <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1', cfg.color)}>
                          <StatusIcon className="h-3 w-3" />{cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {run.recipe?.name} → {run.recipe?.finishedProduct?.name} · {run.location?.name}
                      </p>
                      <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                        <span>Planned: {run.scheduledQuantity}</span>
                        {run.actualYield != null && <span>Actual: {run.actualYield}</span>}
                        <span>Cost: {formatCurrency(Number(run.plannedCost ?? 0))}</span>
                        {run.variancePercent != null && (
                          <span className={Number(run.variancePercent) > 5 ? 'text-destructive-text' : 'text-success-text'}>
                            Variance: {Number(run.variancePercent).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={e => e.stopPropagation()}>
                      {run.status === 'SCHEDULED' && (
                        <Button size="sm" variant="outline" className="gap-1 h-8"
                          onClick={() => startMutation.mutate(run.id)}>
                          <Play className="h-3 w-3" /> Start
                        </Button>
                      )}
                      {run.status === 'IN_PROGRESS' && (
                        <Button size="sm" variant="default" className="gap-1 h-8"
                          onClick={() => navigate(`/manufacturing/production/${run.id}`)}>
                          <CheckCircle2 className="h-3 w-3" /> Complete
                        </Button>
                      )}
                      {['SCHEDULED', 'ON_HOLD'].includes(run.status) && (
                        <Button size="sm" variant="ghost" className="gap-1 h-8 text-destructive-text hover:text-destructive-text"
                          onClick={() => setConfirmCancelId(run.id)}>
                          <XCircle className="h-3 w-3" /> Cancel
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {(data?.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data?.totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= (data?.totalPages ?? 1)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header="Schedule Production Run"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '45vw' }}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSchedule} disabled={scheduleMutation.isPending}>
              {scheduleMutation.isPending ? 'Scheduling…' : 'Schedule Run'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-1 block">Recipe *</Label>
            {recipes.length === 0
              ? <p className="text-sm text-warning-text">No active recipes. <a href="/manufacturing/recipes" className="underline">Create one first.</a></p>
              : <Dropdown
                  className="w-full"
                  value={form.recipeId}
                  onChange={e => setForm(f => ({ ...f, recipeId: e.value }))}
                  options={[
                    { label: '-- Select recipe --', value: '' },
                    ...recipes.map((r: RecipeOption) => ({ label: `${r.name} → ${r.finishedProduct?.name}`, value: r.id })),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="-- Select recipe --"
                />
            }
          </div>
          <div>
            <Label className="mb-1 block">Location *</Label>
            <Dropdown
              className="w-full"
              value={form.locationId}
              onChange={e => setForm(f => ({ ...f, locationId: e.value }))}
              options={[
                { label: '-- Select location --', value: '' },
                ...locations.map((l: LocationOption) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder="-- Select location --"
            />
          </div>
          <div>
            <Label className="mb-1 block">Quantity to Produce *</Label>
            <Input type="number" min="1" step="1"
              value={form.scheduledQuantity}
              onChange={e => setForm(f => ({ ...f, scheduledQuantity: e.target.value }))}
            />
          </div>
          <div>
            <Label className="mb-1 block">Notes (optional)</Label>
            <Textarea
              className="resize-none"
              rows={2} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any special instructions…"
            />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmCancelId}
        onClose={() => setConfirmCancelId(null)}
        onConfirm={() => {
          if (confirmCancelId) cancelMutation.mutate(confirmCancelId);
          setConfirmCancelId(null);
        }}
        title="Cancel Production Run"
        description="This will cancel the production run. Any partially consumed materials will not be automatically restocked. Are you sure?"
        confirmLabel="Cancel Run"
        variant="danger"
      />
    </div>
  );
}
